// What a site's declared languages MEAN for a route, in one place because two
// very different pieces of code have to agree about it — the same argument
// ./url makes for the public render URLs.
//
// `model/page.translate` derives the new page's route from this when the author
// leaves the field empty. The translate SCREEN shows the author that route
// before they submit, which is the only way "leave it empty" is a decision
// rather than a leap. A preview computed from a second implementation is one
// that agrees with the server on every case anyone thought to try and diverges
// on the first one nobody did — and it diverges silently, because the screen
// has no way to learn what the server actually did with the field it dropped.

// The prefix a site declares for a code, or "" for a code it does not declare
// and for one declared without a prefix.
//
// "" is a real answer and not a failure: the default language of a site is
// routinely the one with no prefix at all — `/pricing` is the English page —
// and `translatedRoute` below relies on that being the same value in both
// directions.
export const localePrefix = (site, code) => {
  const locales = (site && site.locales) || [];
  const found = locales.find((entry) => entry && entry.code === code);

  return (found && found.prefix) || "";
};

// The route the translation of a page lands on, when the author does not say.
//
// A site declares a prefix per locale, so this is a prefix SWAP and not a
// prepend: the Spanish translation of `/pricing` is `/es/pricing`, and the
// English translation of `/es/precios` is `/precios` rather than `//es/precios`.
// Stripping the source's own prefix is what makes the operation symmetric, so
// translating back and forth does not accrete segments.
//
// It is only ever a DEFAULT. `/es/precios` — a slug in the target language — is
// most of the reason to have translations at all, and no derivation can produce
// it; the author supplies `route` and this is not consulted.
export const translatedRoute = (site, page, locale) => {
  const from = localePrefix(site, (page && page.locale) || (site && site.defaultLocale));
  const to = localePrefix(site, locale);
  const route = (page && page.route) || "/";

  // Only when it is a whole SEGMENT. A source prefix of `/es` must not eat the
  // first three characters of `/estimates`.
  const bare =
    from && (route === from || route.startsWith(`${from}/`))
      ? route.slice(from.length) || "/"
      : route;

  if (!to) {
    return bare;
  }

  return bare === "/" ? to : `${to}${bare}`;
};
